import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import type { Question } from "@/features/qualifications/types";
import { sectionsOf } from "@/features/qualifications/utils/groups";
import { useTranslation } from "@/features/utils/i18n";

interface QuestionFieldProps {
  question: Question;
  value: unknown;
  onChange: (value: unknown) => void;
}

/**
 * The input matching a question's type. `number` yields a number or `null`
 * (never an empty string), because the required-field check upstream treats
 * `null` as unanswered and `""` as answered.
 */
const QuestionInput = ({ question, value, onChange }: QuestionFieldProps) => {
  const { t } = useTranslation("qualifications");
  switch (question.type) {
    case "boolean":
      return (
        <div className="flex items-center space-x-2">
          <Switch
            id={question.id}
            checked={value === true}
            onCheckedChange={onChange}
          />
          <Label htmlFor={question.id}>
            {value ? t("submissions.common.yes") : t("submissions.common.no")}
          </Label>
        </div>
      );

    case "select":
      return (
        <Select value={String(value || "")} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue
              placeholder={t("submissions.question.select_option")}
            />
          </SelectTrigger>
          <SelectContent>
            {question.options?.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case "number":
      return (
        <Input
          type="number"
          value={value !== undefined && value !== null ? String(value) : ""}
          onChange={(e) =>
            onChange(e.target.value ? Number(e.target.value) : null)
          }
          min={question.min}
          max={question.max}
          placeholder={t("submissions.question.enter_number")}
        />
      );

    case "text":
      return (
        <Textarea
          value={String(value || "")}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t("submissions.question.enter_answer")}
          rows={3}
        />
      );

    default:
      return (
        <Input
          value={String(value || "")}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t("submissions.question.enter_answer")}
        />
      );
  }
};

/**
 * One question of a qualification form: its label, its help text and the input
 * its type calls for. Shared by the create dialog and the edit-responses
 * dialog, which render the same fields against different mutations.
 */
export const QuestionField = ({
  question,
  value,
  onChange,
}: QuestionFieldProps) => (
  <div className="space-y-2">
    <Label htmlFor={question.id}>
      {question.label}
      {question.required && <span className="text-red-500 ml-1">*</span>}
    </Label>
    {question.description && (
      <p className="text-sm text-muted-foreground">{question.description}</p>
    )}
    <QuestionInput question={question} value={value} onChange={onChange} />
  </div>
);

/** Every question's field, under the title of its group when the form has some. */
export function QuestionFields({
  questions,
  responses,
  onChange,
}: {
  questions: Question[];
  responses: Record<string, unknown>;
  onChange: (responses: Record<string, unknown>) => void;
}) {
  return (
    <>
      {sectionsOf(questions, (q) => q.group).map((section) => (
        <div key={section.title || "__ungrouped"}>
          {section.title && (
            <h4 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground first:mt-0">
              {section.title}
            </h4>
          )}
          {section.items.map((question) => (
            <QuestionField
              key={question.id}
              question={question}
              value={responses[question.id]}
              onChange={(value) =>
                onChange({ ...responses, [question.id]: value })
              }
            />
          ))}
        </div>
      ))}
    </>
  );
}
